import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocalParticipant,
  useParticipants,
  useTracks,
} from "@livekit/components-react";
import { ParticipantEvent, Track } from "livekit-client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { SuiteApiError } from "@weco/suite-client";
import { videoService } from "../api/video.api";
import { MeetingControls, type SidePanelTab } from "./MeetingControls";
import { SidePanel } from "./SidePanel";
import { ReactionsOverlay } from "./ReactionsOverlay";
import { SpeakerLayout } from "./SpeakerLayout";
import { GridLayout } from "./GridLayout";
import { SidebarLayout } from "./SidebarLayout";
import { InCallChatContent } from "./InCallChatContent";
import { InCallInviteContent } from "./InCallInviteContent";
import { InCallParticipantsContent } from "./InCallParticipantsContent";
import { useRoom } from "../hooks/useVideo";
import { useParticipantRole } from "../hooks/useParticipantRole";
import { useCallUiState } from "../hooks/useCallUiState";
import { useDeviceToggles } from "../hooks/useDeviceToggles";
import { LocalCameraAffordanceProvider } from "../hooks/useLocalCameraAffordance";
import { ReactionsProvider } from "../hooks/useTileReaction";
import { useCallBroadcast } from "../hooks/useCallBroadcast";
import { useInCallChatNotifications } from "../hooks/useInCallChatNotifications";
import { useAudioOutputControl } from "../hooks/useAudioOutputControl";
import { useInCallDevices } from "../hooks/CallDevicesContext";
import { useWaitingGuests } from "../hooks/useWaitingGuests";
import { useAdmitPolicyChanges } from "../hooks/useAdmitPolicyChanges";
import { playReaction, playToggle } from "../lib/sound";
import { canRingColleaguesIntoCall } from "../lib/ringSelection";
import { useOutgoingRings } from "../hooks/useOutgoingRings";
import { useSendMochi } from "../hooks/useSendMochi";
import { useTwinApprovalPrompt } from "../hooks/useTwinApprovalPrompt";
import { WaitingRoomBanner } from "./WaitingRoomBanner";
import { AdmissionNotices } from "./AdmissionNotices";
import { LoneParticipantDialog } from "./LoneParticipantDialog";
import { useLoneParticipantPrompt } from "../hooks/useLoneParticipantPrompt";
import { enterImmersive, exitImmersive } from "../../../lib/immersiveMode";

interface InCallViewProps {
  roomName: string;
  /** Backend room UUID — required to drive the chat REST endpoints. */
  roomId: string;
  /** LiveKit JWT for backend `X-LiveKit-Token` auth (chat send/history). */
  liveKitToken: string;
  /** Epoch-ms timestamp of when the user clicked Join — feeds the in-call
   *  duration ticker. Comes from useCallSessionStore so the ticker stays
   *  continuous across navigate-away / return-to-call cycles. */
  joinedAt: number;
  /** AudioContext created at join time — used for runtime speaker/earpiece
   *  toggling on iOS and as the webAudioMix context on desktop. */
  audioContext?: AudioContext;
  onLeave: () => void;
}

/**
 * In-call meeting layout. Must be rendered inside a <LiveKitRoom> provider
 * so the LiveKit hooks resolve to the active connection.
 */
export function InCallView({ roomName, roomId, liveKitToken, joinedAt, audioContext, onLeave }: InCallViewProps) {
  const { t } = useTranslation();
  const [activePanel, setActivePanel] = useState<SidePanelTab>(null);

  // Mochi hides for the lifetime of an active call.
  useEffect(() => {
    enterImmersive();
    return () => exitImmersive();
  }, []);

  const {
    viewMode,
    setViewMode,
    handRaised,
    toggleHandRaised,
    reactions,
    emitReaction,
    recording,
    toggleRecording,
  } = useCallUiState();

  const handleToggleRecording = () => {
    const next = !recording;
    toggleRecording();
    toast(next ? t("call.controls.recordingStartedToast") : t("call.controls.recordingStoppedToast"));
  };

  const participants = useParticipants();

  const {
    isMicrophoneEnabled,
    isCameraEnabled,
    isScreenShareEnabled,
    localParticipant,
  } = useLocalParticipant();
  const micOn = isMicrophoneEnabled;
  const cameraOn = isCameraEnabled;
  const screenSharing = isScreenShareEnabled;

  // #215 — session role, read from the local participant's LiveKit
  // attributes (minted server-side, updated live via the promote/demote
  // endpoint). Drives admin-only affordances (replacing the previous
  // room-ownership-only `isHost` gate) and the guest screen-share gate.
  const myRole = useParticipantRole(localParticipant);
  const isAdmin = myRole === "admin";
  const isGuestRole = myRole === "guest";

  // AC8 — if a live demote drops the local user to guest while they are
  // actively screen-sharing, the grant is revoked server-side but LiveKit
  // does not auto-stop an already-published track; stop it explicitly so
  // the UI and the actual publish state stay in sync.
  useEffect(() => {
    if (isGuestRole && screenSharing) {
      void localParticipant?.setScreenShareEnabled(false);
    }
  }, [isGuestRole, screenSharing, localParticipant]);

  const { publishReaction } = useCallBroadcast({
    handRaised,
    onRemoteReaction: (emoji, sender) =>
      emitReaction(emoji, {
        identity: sender.identity,
        name: sender.name?.trim() || prettyIdentity(sender.identity),
        isLocal: false,
      }),
  });

  const handleEmitReaction = (emoji: string) => {
    if (!localParticipant) return;
    playReaction();
    emitReaction(emoji, {
      identity: localParticipant.identity,
      name: t("call.controls.reactedYou"),
      isLocal: true,
    });
    publishReaction(emoji);
  };

  // Detect a remote sharer so the local button can disable while the slot is held.
  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  const someoneElseSharing = screenShareTracks.some(
    (t) => t.participant.identity !== localParticipant?.identity,
  );

  // Error-aware toggles (GH-1179): surface a persistent affordance when the
  // camera/mic can't be acquired instead of the previous silent `void set…`.
  const { cameraError, micError, toggleCamera, toggleMic } =
    useDeviceToggles(localParticipant);

  const onToggleMic = () => {
    const next = !micOn;
    void toggleMic(next).then((ok) => {
      if (ok) playToggle(next);
    });
  };
  const onToggleCamera = () => {
    const next = !cameraOn;
    void toggleCamera(next).then((ok) => {
      if (ok) playToggle(next);
    });
  };

  // Mic / speaker / camera selection lives above this view (GH-1859) so it
  // survives minimising the call — see InCallDevicesProvider.
  const callDevices = useInCallDevices();

  // Screen share requires a backend acquire/release dance: the LiveKit JWT
  // only grants camera+microphone by default (`LIVEKIT_DEFAULT_SOURCES`), and
  // `acquireScreenShare` mutates the participant's `can_publish_sources`
  // server-side.
  //
  // On mobile, getDisplayMedia() MUST be called inside the user gesture window.
  // The original order (acquireScreenShare → setScreenShareEnabled) breaks on
  // mobile because the async HTTP call lets the gesture window expire before
  // getDisplayMedia() is invoked. Fix: capture the display media first (within
  // the gesture), then acquire the backend slot, then publish the pre-captured
  // track. On iOS Chrome (WKWebView) getDisplayMedia is unsupported entirely —
  // we detect that upfront and show a clear error instead of silently failing.
  const toggleScreenShare = useCallback(async () => {
    if (!localParticipant) return;
    if (screenSharing) {
      await localParticipant.setScreenShareEnabled(false);
      return;
    }

    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error(t("call.inCallView.errorScreenShareUnsupported"));
      return;
    }

    // Capture display media immediately — still inside the user gesture window.
    let displayStream: MediaStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch (err) {
      const name = (err as DOMException).name;
      if (name !== "AbortError" && name !== "NotAllowedError") {
        console.error("getDisplayMedia failed:", err);
        toast.error(t("call.inCallView.errorScreenCapture"));
      }
      // AbortError / NotAllowedError = user cancelled or denied — no toast needed.
      return;
    }

    // Acquire the backend slot (no gesture needed for HTTP).
    try {
      await videoService.acquireScreenShare(roomId, liveKitToken);
    } catch (err) {
      displayStream.getTracks().forEach((t) => t.stop());
      if (err instanceof SuiteApiError && err.code === 409) {
        const active =
          (err.details as { active_sharer?: { name?: string } } | undefined)
            ?.active_sharer?.name || "Someone";
        toast.error(t("call.inCallView.errorAlreadySharing", { name: active }));
        return;
      }
      console.error("Screen-share acquire failed:", err);
      toast.error(t("call.inCallView.errorStartShare"));
      return;
    }

    // Wait for LiveKit to deliver the ParticipantPermissionsUpdated WS message.
    // acquireScreenShare tells the backend → backend calls LiveKit UpdateParticipant
    // → LiveKit sends ParticipantPermissionsUpdated to the client. This is async and
    // typically arrives ~100–300 ms after the HTTP response. Publishing before it
    // arrives causes "insufficient permissions" on the first attempt. Max wait: 4 s.
    const hasPermission = localParticipant.permissions?.canPublishSources?.includes(
      Track.Source.ScreenShare as unknown as Parameters<typeof localParticipant.permissions.canPublishSources.includes>[0],
    );
    if (!hasPermission) {
      await new Promise<void>((resolve) => {
        const timeout = window.setTimeout(resolve, 4000);
        localParticipant.once(ParticipantEvent.ParticipantPermissionsChanged, () => {
          window.clearTimeout(timeout);
          resolve();
        });
      });
    }

    // Publish the pre-captured track.
    const videoTrack = displayStream.getVideoTracks()[0];
    if (!videoTrack) {
      displayStream.getTracks().forEach((t) => t.stop());
      videoService.releaseScreenShare(roomId, liveKitToken).catch(() => {});
      toast.error(t("call.inCallView.errorCaptureVideo"));
      return;
    }
    try {
      await localParticipant.publishTrack(videoTrack, { source: Track.Source.ScreenShare });
    } catch (publishErr) {
      console.warn("publishTrack failed after acquire; releasing slot:", publishErr);
      videoTrack.stop();
      videoService.releaseScreenShare(roomId, liveKitToken).catch(() => {});
      toast.error(t("call.inCallView.errorStartShare"));
    }
  }, [localParticipant, screenSharing, roomId, liveKitToken, t]);

  // Auto-release on every stop path: explicit stop, Chrome-native "Stop sharing"
  // bar, mid-call disconnect. LiveKit fires `localTrackUnpublished` for all of
  // them, so a single listener covers the lot.
  useEffect(() => {
    if (!localParticipant) return;
    const handleUnpublished = (publication: { source?: Track.Source }) => {
      if (publication.source !== Track.Source.ScreenShare) return;
      videoService.releaseScreenShare(roomId, liveKitToken).catch((err) => {
        console.error("Auto-release after trackUnpublished failed:", err);
      });
    };
    localParticipant.on("localTrackUnpublished", handleUnpublished);
    return () => {
      localParticipant.off("localTrackUnpublished", handleUnpublished);
    };
  }, [localParticipant, roomId, liveKitToken]);

  const participantCount = participants.length;

  // Lone-participant prompt: if you're the only one left for 5 minutes, ask
  // whether to end the call (End / Snooze) instead of dropping you.
  const { promptOpen: loneOpen, snooze: loneSnooze } = useLoneParticipantPrompt(
    participantCount === 1,
  );

  // Auto-promote to Speaker when the participant count crosses ABOVE 5
  // (the 6th person joins, or we join a room that already has 6+). Edge-
  // triggered on the upward crossing only — once promoted, a manual layout
  // change via the popover is respected and never fought. Re-arms if the
  // room shrinks back to <=5 and later grows past 5 again. prevCount starts
  // at 0 so joining straight into a 6+ room still triggers the crossing.
  const prevCountRef = useRef(0);
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = participantCount;
    if (prev <= 5 && participantCount > 5) {
      setViewMode("speaker");
    }
  }, [participantCount, setViewMode]);

  // Auto-promote to Speaker when a screen-share starts (local or remote) —
  // Speaker makes the shared screen the main tile. Edge-triggered on the
  // start of sharing only, so a manual layout change afterward is respected;
  // re-arms when sharing stops and a new share later begins.
  const isScreenSharing = screenShareTracks.length > 0;
  const prevSharingRef = useRef(false);
  useEffect(() => {
    const prev = prevSharingRef.current;
    prevSharingRef.current = isScreenSharing;
    if (!prev && isScreenSharing) {
      setViewMode("speaker");
    }
  }, [isScreenSharing, setViewMode]);

  // In-call chat notifications. The listener lives at this level so it
  // keeps receiving while the chat side-panel is closed (and
  // InCallChatContent is unmounted). Resets when the user opens chat.
  const { unreadCount: chatUnreadCount, reset: resetChatUnread } =
    useInCallChatNotifications({ notifyOnReceive: activePanel !== "chat" });
  useEffect(() => {
    if (activePanel === "chat") resetChatUnread();
  }, [activePanel, resetChatUnread]);

  const audioOutput = useAudioOutputControl(audioContext);

  // Fetch room config to determine whether this user can admit waiting guests.
  // Polling is disabled — the room config changes only when an owner PATCHes it.
  // The owner's own InCallInviteContent invalidates the query; everyone else
  // learns of an admit_policy change from the backend's admitPolicyChanged
  // data message (#1850) and refetches here. Safe mid-call: the LiveKit creds
  // are pinned in useCallSessionStore at Join, not read from this query.
  const { data: room } = useRoom(roomId, { pollIntervalMs: false });
  const queryClient = useQueryClient();
  useAdmitPolicyChanges(() => {
    void queryClient.invalidateQueries({ queryKey: ["video", "room", roomId] });
  });
  const mayAdmitGuests = Boolean(room?.may_admit_guests);
  // GH-2017 — Mochi dispatched into a RESTRICTED room needs an in-room
  // approval before it can join; eligible = same signal as the waiting-room
  // admit gate above.
  useTwinApprovalPrompt(mayAdmitGuests, t);
  // GH-1768 — same link as "Create a link to share" (ShareLinkModal).
  const shareLink = room?.join_url ?? `${window.location.origin}/call/${room?.id ?? roomId}`;

  const {
    waitingParticipants,
    admit: admitGuest,
    deny: denyGuest,
  } = useWaitingGuests(roomId, liveKitToken, mayAdmitGuests);

  const handleToggleHandRaised = () => {
    const next = !handRaised;
    toggleHandRaised();
    toast(next ? t("call.controls.handRaisedToast") : t("call.controls.handLoweredToast"));
  };

  // #1384. InCallView is the *authenticated* in-call surface — guests never
  // render this tree — so isGuestSession is false here by construction.
  const mayRingColleagues = canRingColleaguesIntoCall({ isGuestSession: false });
  const joinedSubs = useMemo(
    () => new Set(participants.map((p) => p.identity)),
    [participants],
  );
  const { invited, pill, ringingSub, ringColleague } = useOutgoingRings(roomId, joinedSubs);
  const { sendMochi } = useSendMochi();

  const peopleContent = (
    <InCallParticipantsContent
      waitingParticipants={waitingParticipants}
      onApprove={(id) => void admitGuest(id)}
      onDeny={(id) => void denyGuest(id)}
      invited={invited}
      canRing={mayRingColleagues}
      ringingSub={ringingSub}
      onRing={(user) => void ringColleague(user)}
      roomId={roomId}
      isAdmin={isAdmin}
    />
  );
  const inviteContent = <InCallInviteContent roomId={roomId} />;

  return (
    <LocalCameraAffordanceProvider value={cameraError}>
    <div className="flex-1 flex flex-col [background:#EEF0F3] dark:[background:oklch(16%_.006_262)] overflow-hidden relative h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-hidden relative">
        <ReactionsProvider value={reactions}>
          {viewMode === "grid" && <GridLayout />}
          {viewMode === "speaker" && <SpeakerLayout />}
          {viewMode === "sidebar" && <SidebarLayout />}
        </ReactionsProvider>

        {recording && (
          <div className="absolute top-4 start-4 z-10 flex items-center gap-2 px-3 py-2 bg-red-500/15 border border-red-500/30 rounded-xl backdrop-blur-sm anim-scale-in">
            <span className="size-2.5 rounded-full bg-red-500 anim-pulse-rec" />
            <span className="text-red-600 text-[10px] font-black uppercase tracking-widest">
              {t("call.controls.recordingBadge")}
            </span>
          </div>
        )}

        <WaitingRoomBanner
          waitingParticipants={waitingParticipants}
          onApprove={(id) => void admitGuest(id)}
          onDeny={(id) => void denyGuest(id)}
        />
        <AdmissionNotices
          roomId={roomId}
          liveKitToken={liveKitToken}
          isOwner={Boolean(room?.is_owner)}
        />

        <ReactionsOverlay reactions={reactions} />
      </div>

      <MeetingControls
        roomName={roomName}
        meetingCode={room?.join_code}
        meetingCodeLoading={!room}
        shareLink={shareLink}
        roomId={roomId}
        participantCount={participantCount}
        waitingCount={waitingParticipants.length}
        joinedAt={joinedAt}
        micOn={micOn}
        micError={micError}
        onToggleMic={onToggleMic}
        callDevices={callDevices}
        cameraOn={cameraOn}
        onToggleCamera={onToggleCamera}
        screenSharing={Boolean(screenSharing)}
        screenShareDisabled={someoneElseSharing && !screenSharing}
        screenShareGuestBlocked={isGuestRole}
        screenShareNotSupported={/android|iphone|ipad|ipod/i.test(navigator.userAgent)}
        onToggleScreenShare={() => void toggleScreenShare()}
        handRaised={handRaised}
        onToggleHandRaised={handleToggleHandRaised}
        recording={recording}
        onToggleRecording={handleToggleRecording}
        activePanel={activePanel}
        onPanelChange={setActivePanel}
        chatUnreadCount={chatUnreadCount}
        // #215 — admin-only affordances (the desktop Settings-gear popover)
        // now follow the session role, not just durable room ownership; a
        // promoted admin sees it too, a demoted owner-as-guest loses it.
        isHost={isAdmin}
        inviteContent={inviteContent}
        ringStatus={pill}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onLeave={onLeave}
        onEmitReaction={handleEmitReaction}
        audioOutput={audioOutput}
        onSendMochi={() => sendMochi({ target_type: "room", target_ref: roomId })}
      />

      <LoneParticipantDialog
        open={loneOpen}
        onEndCall={onLeave}
        onSnooze={loneSnooze}
      />

      <SidePanel
        activeTab={activePanel}
        onClose={() => setActivePanel(null)}
        chatContent={
          <InCallChatContent roomId={roomId} liveKitToken={liveKitToken} />
        }
        usersContent={peopleContent}
        inviteContent={inviteContent}
        participantCount={participantCount}
      />
    </div>
    </LocalCameraAffordanceProvider>
  );
}

function prettyIdentity(identity: string): string {
  return identity.split("@")[0];
}
